import { Component, Input, OnInit } from '@angular/core';
import {
  EdiMappingDryRun,
  EdiMappingDryRunBatch,
  EdiMappingDryRunResult,
  EdiMappingInstruction,
  EdiMappingModel,
  EdiMappingPrompt,
  EdiSavedMappingModel,
  EdiSavedMappingModelSummary,
  EdiMappingValidation,
  EdiTargetField
} from '../../../models/edi-mapping-modeller';
import { EdiSourceNode } from '../../../models/edi-viewer';
import { EdiService } from '../../../services/edi/edi.service';
import { EdiMappingModellerCompatibilityService } from '../../../services/edi/edi-mapping-modeller-compatibility.service';
import { Observable } from 'rxjs';

type MapperWorkspaceTab = 'map' | 'instruction' | 'test' | 'history';

@Component({
  selector: 'lib-edi-mapping-modeller',
  templateUrl: './edi-mapping-modeller.component.html',
  styleUrls: ['./edi-mapping-modeller.component.scss'],
  standalone: false
})
export class EdiMappingModellerComponent implements OnInit {
  targetMessageTypes: string[] = [];
  targetMessageType = 'ORDER';
  @Input() selectedFile?: File;
  sourceFiles: File[] = [];
  useAi = true;
  userInstruction = '';
  activeWorkspaceTab: MapperWorkspaceTab = 'map';
  loading = false;
  validating = false;
  dryRunning = false;
  error?: string;
  model?: EdiMappingModel;
  validation?: EdiMappingValidation;
  dryRun?: EdiMappingDryRun;
  dryRunBatch?: EdiMappingDryRunBatch;
  selectedDryRunIndex = 0;
  dryRunJson = '';
  ruleResultsExpanded = false;
  dryRunOutdated = false;
  dryRunCompatible = false;
  compatibilityWarning?: string;
  instructionJson = '';
  sourceNodeIndex: Record<string, EdiSourceNode> = {};
  selectedSourceNode?: EdiSourceNode;
  selectedSourceNodeIds: ReadonlySet<string> = new Set<string>();
  selectedTargetField?: EdiTargetField;
  savedModels: EdiSavedMappingModelSummary[] = [];
  selectedSavedModelId = '';
  savedModel?: EdiSavedMappingModel;
  modelName = '';
  sessionPrompts: string[] = [];
  loadingSavedModel = false;
  savingModel = false;

  constructor(
    private ediService: EdiService,
    private compatibilityService: EdiMappingModellerCompatibilityService
  ) {}

  ngOnInit(): void {
    this.compatibilityService.getCompatibility().subscribe(result => {
      this.dryRunCompatible = result.compatible;
      this.compatibilityWarning = result.message;
      if (result.compatible) this.loadSavedModels();
    });
    this.ediService.getMappingModellerTargetTypes().subscribe({
      next: types => {
        this.targetMessageTypes = types;
        if (!types.includes(this.targetMessageType)) this.targetMessageType = types[0];
      },
      error: error => (this.error = this.errorMessage(error, 'Could not load runtime Gung EDI target types.'))
    });
  }

  selectFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;
    this.sourceFiles = files;
    this.selectedFile = files[0];
    this.model = undefined;
    this.instructionJson = '';
    this.validation = undefined;
    this.clearDryRun();
    this.error = undefined;
  }

  addSourceFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const additions = Array.from(input.files || []);
    if (!additions.length) return;
    const existing = this.fixtureFiles;
    const keys = new Set(existing.map(file => this.fileKey(file)));
    this.sourceFiles = [...existing, ...additions.filter(file => !keys.has(this.fileKey(file)))];
    this.selectedFile ||= this.sourceFiles[0];
    this.clearDryRun();
    input.value = '';
  }

  removeSourceFile(index: number): void {
    const files = this.fixtureFiles;
    if (index <= 0 || index >= files.length) return;
    this.sourceFiles = files.filter((_, candidate) => candidate !== index);
    this.clearDryRun();
  }

  createModel(updateExisting = false): void {
    if (!this.selectedFile || !this.targetMessageType) return;
    if (updateExisting && !this.parseInstruction()) return;
    this.loading = true;
    this.error = undefined;
    const submittedPrompt = this.useAi ? this.userInstruction.trim() : '';
    this.ediService
      .modelMappingInstruction(
        this.selectedFile,
        this.targetMessageType,
        this.useAi,
        this.userInstruction,
        updateExisting ? this.instructionJson : undefined
      )
      .subscribe({
        next: model => {
          this.loading = false;
          if (submittedPrompt) this.sessionPrompts.push(submittedPrompt);
          this.prepareModel(model);
          if (updateExisting) this.activeWorkspaceTab = 'instruction';
        },
        error: error => {
          this.loading = false;
          this.error = this.errorMessage(error, 'Could not create the mapping model.');
        }
      });
  }

  validateInstruction(): void {
    if (!this.selectedFile || !this.parseInstruction()) return;
    this.validating = true;
    this.error = undefined;
    this.ediService
      .validateMappingInstruction(this.selectedFile, this.targetMessageType, this.instructionJson)
      .subscribe({
        next: validation => {
          this.validation = validation;
          this.activeWorkspaceTab = 'instruction';
          this.validating = false;
        },
        error: error => {
          this.validating = false;
          this.error = this.errorMessage(error, 'Could not validate the mapping instruction.');
        }
      });
  }

  dryRunInstruction(): void {
    const files = this.fixtureFiles;
    if (!this.dryRunCompatible || !files.length || !this.parseInstruction()) return;
    this.dryRunning = true;
    this.error = undefined;
    this.ediService.dryRunMappingInstructionBatch(files, this.targetMessageType, this.instructionJson).subscribe({
      next: batch => {
        this.dryRunBatch = batch;
        const firstFailure = batch.results.findIndex(result => !result.dryRun.successful);
        this.selectDryRunResult(firstFailure >= 0 ? firstFailure : 0);
        this.dryRunOutdated = false;
        this.activeWorkspaceTab = 'test';
        this.dryRunning = false;
      },
      error: error => {
        this.dryRunning = false;
        this.error = this.errorMessage(error, 'Could not dry-run the mapping instruction.');
      }
    });
  }

  selectDryRunResult(index: number): void {
    const result = this.dryRunBatch?.results[index];
    if (!result) return;
    this.selectedDryRunIndex = index;
    this.dryRun = result.dryRun;
    this.validation = result.dryRun.validation;
    this.dryRunJson = result.dryRun.result == null ? '' : JSON.stringify(result.dryRun.result, null, 2);
  }

  dryRunResultName(result?: EdiMappingDryRunResult): string {
    return result?.fileName || 'Unnamed source';
  }

  selectSource(ids: string[]): void {
    this.selectedSourceNode = ids.map(id => this.sourceNodeIndex[id]).find(node => !!node);
    this.selectedSourceNodeIds = new Set(this.selectedSourceNode ? [this.selectedSourceNode.id] : []);
  }

  selectTarget(field: EdiTargetField): void {
    if (field.mappable) this.selectedTargetField = field;
  }

  addMapRule(): void {
    if (!this.selectedSourceNode || !this.selectedTargetField) return;
    const instruction = this.parseInstruction();
    if (!instruction) return;
    instruction.rules ||= [];
    instruction.rules.push({
      id: this.nextRuleId(instruction),
      operation: 'MAP',
      sourceSelector: this.selectedSourceNode.path,
      targetPath: this.selectedTargetField.path,
      value: null,
      condition: {},
      lookup: {},
      description: `Map ${this.selectedSourceNode.label} to ${this.selectedTargetField.label}`,
      children: [],
      transforms: []
    });
    this.instructionJson = JSON.stringify(instruction, null, 2);
    this.validation = undefined;
    this.markDryRunOutdated();
  }

  instructionChanged(): void {
    this.validation = undefined;
    this.markDryRunOutdated();
  }

  selectWorkspaceTab(tab: MapperWorkspaceTab): void {
    this.activeWorkspaceTab = tab;
  }

  toggleRuleResults(): void {
    this.ruleResultsExpanded = !this.ruleResultsExpanded;
  }

  selectSavedModel(modelId: string): void {
    if (!modelId) {
      this.startNewModel();
      return;
    }
    this.loadingSavedModel = true;
    this.error = undefined;
    this.ediService.getSavedMappingModel(modelId).subscribe({
      next: saved => this.hydrateSavedModel(saved),
      error: error => {
        this.loadingSavedModel = false;
        this.error = this.errorMessage(error, 'Could not load the saved mapping model.');
      }
    });
  }

  startNewModel(): void {
    this.selectedSavedModelId = '';
    this.savedModel = undefined;
    this.sessionPrompts = [];
    this.modelName = this.model ? this.suggestedModelName(this.model) : '';
  }

  saveModel(): void {
    const files = this.fixtureFiles;
    if (!this.dryRunCompatible || !files.length || !this.model || !this.parseInstruction()) return;
    const name = this.modelName.trim();
    if (!this.savedModel && !name) {
      this.error = 'A model name is required before the first version can be saved.';
      return;
    }
    this.savingModel = true;
    this.error = undefined;
    const save: Observable<EdiSavedMappingModel> = this.savedModel
      ? this.ediService.createSavedMappingModelVersion(
          this.savedModel.modelId,
          this.savedModel.version,
          files,
          this.targetMessageType,
          this.instructionJson,
          this.sessionPrompts
        )
      : this.ediService.createSavedMappingModel(
          name,
          files,
          this.targetMessageType,
          this.instructionJson,
          this.sessionPrompts
        );
    save.subscribe({
      next: saved => {
        this.savingModel = false;
        this.applySavedState(saved);
        this.loadSavedModels();
      },
      error: error => {
        this.savingModel = false;
        this.error = this.errorMessage(error, 'Could not save the mapping model.');
      }
    });
  }

  get savedPrompts(): EdiMappingPrompt[] {
    return this.savedModel?.prompts || [];
  }

  private prepareModel(model: EdiMappingModel): void {
    this.model = model;
    this.validation = model.validation;
    this.instructionJson = JSON.stringify(model.instruction, null, 2);
    this.sourceNodeIndex = {};
    for (const root of model.source.roots) this.indexSourceNode(root);
    this.selectedSourceNode = undefined;
    this.selectedSourceNodeIds = new Set<string>();
    this.selectedTargetField = undefined;
    this.clearDryRun();
    this.activeWorkspaceTab = 'map';
    if (!this.savedModel && !this.modelName.trim()) this.modelName = this.suggestedModelName(model);
  }

  private hydrateSavedModel(saved: EdiSavedMappingModel): void {
    const sources = saved.sources?.length
      ? saved.sources
      : saved.sample
        ? [
            {
              ...saved.sample,
              id: saved.sample.sourceId,
              modelId: saved.modelId,
              sizeBytes: saved.sample.rawContent?.length || 0,
              sha256: saved.sample.sourceId,
              createdAt: saved.createdAt,
              createdBy: saved.createdBy
            }
          ]
        : [];
    const primaryId = saved.primarySourceDocumentId;
    const ordered = [...sources].sort(
      (first, second) => Number(second.id === primaryId) - Number(first.id === primaryId)
    );
    this.sourceFiles = ordered.map(
      source =>
        new File([source.rawContent], source.fileName || 'edi-document.txt', { type: source.mediaType || 'text/plain' })
    );
    const file = this.sourceFiles[0];
    if (!file) {
      this.loadingSavedModel = false;
      this.error = 'The saved mapping model does not contain a source document.';
      return;
    }
    this.selectedFile = file;
    this.targetMessageType = saved.targetMessageType;
    this.modelName = saved.name;
    this.userInstruction = '';
    this.sessionPrompts = [];
    this.ediService
      .modelMappingInstruction(file, saved.targetMessageType, false, undefined, JSON.stringify(saved.instruction))
      .subscribe({
        next: model => {
          this.applySavedState(saved);
          this.prepareModel(model);
          this.loadingSavedModel = false;
        },
        error: error => {
          this.loadingSavedModel = false;
          this.error = this.errorMessage(error, 'Could not rebuild the saved model against the runtime contract.');
        }
      });
  }

  private applySavedState(saved: EdiSavedMappingModel): void {
    this.savedModel = saved;
    this.selectedSavedModelId = saved.modelId;
    this.modelName = saved.name;
    this.targetMessageType = saved.targetMessageType;
    this.instructionJson = JSON.stringify(saved.instruction, null, 2);
    this.validation = saved.validation;
    this.sessionPrompts = [];
  }

  private loadSavedModels(): void {
    this.ediService.getSavedMappingModels().subscribe({
      next: models => (this.savedModels = models),
      error: error => (this.error = this.errorMessage(error, 'Could not load saved mapping models.'))
    });
  }

  private suggestedModelName(model: EdiMappingModel): string {
    const source = [model.source.standard || model.source.syntax, model.source.messageType]
      .filter(value => !!value)
      .join(' ');
    const fallback = model.source.fileName?.replace(/\.[^.]+$/, '') || 'EDI document';
    return `${source || fallback} to Gung ${model.target.messageType}`;
  }

  private clearDryRun(): void {
    this.dryRun = undefined;
    this.dryRunBatch = undefined;
    this.selectedDryRunIndex = 0;
    this.dryRunJson = '';
    this.dryRunOutdated = false;
  }

  private markDryRunOutdated(): void {
    if (this.dryRunBatch || this.dryRun) this.dryRunOutdated = true;
  }

  get fixtureFiles(): File[] {
    if (this.sourceFiles.length) return this.sourceFiles;
    return this.selectedFile ? [this.selectedFile] : [];
  }

  private fileKey(file: File): string {
    return `${file.name}:${file.size}:${file.lastModified}`;
  }

  private indexSourceNode(node: EdiSourceNode): void {
    this.sourceNodeIndex[node.id] = node;
    for (const child of node.children || []) this.indexSourceNode(child);
  }

  private parseInstruction(): EdiMappingInstruction | undefined {
    try {
      const instruction = JSON.parse(this.instructionJson) as EdiMappingInstruction;
      this.error = undefined;
      return instruction;
    } catch (error) {
      this.error = `The mapping instruction is not valid JSON: ${(error as Error).message}`;
      return undefined;
    }
  }

  private nextRuleId(instruction: EdiMappingInstruction): string {
    const existing = new Set(instruction.rules.map(rule => rule.id));
    let sequence = instruction.rules.length + 1;
    while (existing.has(`rule-${sequence}`)) sequence++;
    return `rule-${sequence}`;
  }

  private errorMessage(error: any, fallback: string): string {
    return error?.error?.detail || error?.error?.message || fallback;
  }
}
